/*
 * Назначение: блок «Клонировать проект» во вкладке «Общие».
 * Функционал: создаёт новый проект-копию текущего (опционально с базой
 * знаний и менеджерами). Токены каналов специально не копируются —
 * каждый проект подключает свои.
 */

import { useState } from "react";
import { AlertCircle, Copy, Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { useProject } from "../../components/project/project-provider";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import type { ProjectDetails } from "../../../lib/types";

export function CloneProject({ project }: { project: ProjectDetails }) {
  const queryClient = useQueryClient();
  const { setCurrent } = useProject();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [copyKnowledge, setCopyKnowledge] = useState(true);
  const [copyManagers, setCopyManagers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cloneMutation = useMutation({
    mutationFn: () =>
      projectsApi.clone({
        source_slug: project.slug,
        slug: slug.trim(),
        name: name.trim(),
        copy_knowledge: copyKnowledge,
        copy_managers: copyManagers,
      }),
    onSuccess: async (created) => {
      toast.success(`Проект «${created.name}» создан копированием`);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setOpen(false);
      setName("");
      setSlug("");
      setCurrent(created.slug);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.detail : "Не удалось клонировать проект");
    },
  });

  const handleClone = () => {
    setError(null);
    if (name.trim().length < 1) {
      setError("Укажи название нового проекта");
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$|^[a-z0-9]$/.test(slug.trim())) {
      setError("Код проекта — латиница, цифры и дефис (например, my-shop)");
      return;
    }
    cloneMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Copy className="h-5 w-5" />
          Клонировать проект
        </CardTitle>
        <CardDescription>
          Создать новый проект на основе этого: скопируются настройки и,
          по желанию, база знаний. Удобно для похожего бизнеса — потом
          отредактируешь под него.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          onClick={() => {
            setName("");
            setSlug("");
            setCopyKnowledge(true);
            setCopyManagers(false);
            setError(null);
            setOpen(true);
          }}
        >
          <Copy className="mr-2 h-4 w-4" />
          Клонировать «{project.name}»
        </Button>
      </CardContent>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o && !cloneMutation.isPending) setOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Клонировать проект «{project.name}»</DialogTitle>
            <DialogDescription>
              Будет создан новый проект-копия. Боты и токены каналов НЕ
              копируются — их укажешь отдельно для нового проекта.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clone-name">Название нового проекта</Label>
              <Input
                id="clone-name"
                autoFocus
                placeholder="Например: Веб-студия №2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={cloneMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clone-slug">Код проекта (латиницей)</Label>
              <Input
                id="clone-slug"
                placeholder="my-shop"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                disabled={cloneMutation.isPending}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="clone-kb"
                checked={copyKnowledge}
                onCheckedChange={(v) => setCopyKnowledge(Boolean(v))}
              />
              <Label htmlFor="clone-kb">Скопировать базу знаний</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="clone-mgr"
                checked={copyManagers}
                onCheckedChange={(v) => setCopyManagers(Boolean(v))}
              />
              <Label htmlFor="clone-mgr">Скопировать менеджеров (контакты уведомлений)</Label>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={cloneMutation.isPending}
            >
              Отмена
            </Button>
            <Button onClick={handleClone} disabled={cloneMutation.isPending}>
              {cloneMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              Клонировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
